import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import styled from 'styled-components';
import { translate } from 'translations/i18n';
import { Text } from '@companycam/slab-web';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LD_WORKFLOWS_PAGE_TEMPLATES } from 'constants/featureFlags';
import { ADMIN, MANAGER, hasRoleAccess } from 'helpers/roles';

import steven from 'helpers/steven';

import PageToolbar from './PageToolbar';
import PageAssetPickerModal from './assetPicker/PageAssetPickerModal';
import ProjectPageShareActions from './ProjectPageShareActions';
import ProjectPageMenuActions from './ProjectPageMenuActions';
import PageTranscript from './PageTranscript';

// TipTap
import {
  useTiptapEditorLegacy,
  WebEditor,
  WebEditorElement,
} from '@companycam/tiptap-editor';

import CCTooltip from 'components/shared/DesignSystem/CCFloating/CCTooltip';
import useLocalStorage from 'javascripts/hooks/useLocalStorage';
import { useCurrentUser } from 'javascripts/hooks';
import { debounce } from 'lodash';
import { formatDate } from 'helpers/intl-helper';
import {
  graphQLIdGenerator,
  graphQLIdFromProjectDBID,
} from 'helpers/graphql-utils';
import PagePdfExportModal from './PagePdfExportModal';
import usePagePdfExport, {
  PagePdfExportConfig,
} from './hooks/usePagePdfExport';
import { useUpdateProjectPage } from 'data/v3';
import { PageObject } from 'data/v3/data-contracts';

const TipTapContainer = styled.div`
  margin: 0 var(--cc_size_spacing_l);
  padding-bottom: var(--cc_size_spacing_l);
  margin-top: var(--cc_size_spacing_l);
  flex: 1;
`;

const PageHeader = styled.div`
  position: sticky;
  top: 0;
  padding: var(--cc_size_spacing_s) var(--cc_size_spacing_xs)
    var(--cc_size_spacing_s) var(--cc_size_spacing_m);
  background-color: white;
  border-bottom-width: var(--cc_size_border_width_s);
  border-bottom-style: solid;
  border-bottom-color: var(--cc_color_border_default);
  z-index: 200;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const PageStatusDiv = styled.div`
  padding: 0 var(--cc_size_spacing_s);
  margin-left: auto;
`;

const HeaderTitle = styled.div`
  font-weight: 400;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 1; /* Number of lines to show */
  -webkit-box-orient: vertical;
  max-width: 50%;
`;

const SaveIndicator = styled.div`
  display: flex;
  align-items: center;
  color: var(--cc_color_utility_success);
`;

export type ProjectPageProps = {
  page: PageObject;
  projectDbId: number;
  onDelete: () => void;
};

/**
 * @deprecated Use ProjectPage instead
 */

const ProjectPage = ({
  page,
  projectDbId,
  onDelete = () => {},
}: ProjectPageProps) => {
  const flags = useFlags();
  const isPageTemplatesEnabled = flags[LD_WORKFLOWS_PAGE_TEMPLATES];

  const currentUser = useCurrentUser();
  const canSaveAsTemplate = hasRoleAccess(currentUser, [ADMIN, MANAGER]);

  const projectNodeId = graphQLIdFromProjectDBID(projectDbId);
  const pageNodeId = graphQLIdGenerator('Page', page.id);

  const containerRef = useRef(null);
  const [isShowingTranscript, setIsShowingTranscript] = useState(false);
  const [isPdfExportOpen, setIsPdfExportOpen] = useState(false);

  const captureOutputRequest = page?.captureOutputRequest;
  const transcripts = captureOutputRequest?.transcripts;
  const hasTranscripts = transcripts && transcripts.length > 0;

  const [pageTitle, setPageTitle] = useState(page?.title);
  const [showAutoSave, setShowAutoSave] = useState(false);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const webEditorRef = useRef<WebEditorElement | null>(null);

  const { mutate: updatePage } = useUpdateProjectPage({
    pageDbId: page.id,
    projectDbId,
  });

  const { handlePdfExport, isExporting } = usePagePdfExport();

  const [hasOnboardedPageTemplates, setHasOnboardedPageTemplates] =
    useLocalStorage(
      `hasOnboardedPageTemplatesModal:${currentUser.node_id}`,
      false,
    );

  useEffect(() => {
    let timer: any = null;
    if (showAutoSave) {
      timer = setTimeout(() => {
        setShowAutoSave(false);
      }, 3000);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [showAutoSave]);

  useEffect(() => {
    if (isShowingTranscript) {
      // scroll to top
      containerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isShowingTranscript]);

  const handleSave = useCallback(
    ({ title, content }: { title: string; content: any }) => {
      updatePage({
        title,
        tiptap_content: content,
      });
      setShowAutoSave(true);
    },
    [updatePage],
  );

  const debouncedOnChange = useMemo(
    () => debounce(handleSave, 1000),
    [handleSave],
  );

  // flush the debouncedOnChange when the component is unmounted
  useEffect(() => {
    return debouncedOnChange.flush;
  }, [debouncedOnChange]);

  const { editor: tiptapEditor } = useTiptapEditorLegacy({
    initialContent:
      page?.tiptapContent?.content?.length > 0 ? page.tiptapContent : null,
    onUpdate({ editor }) {
      debouncedOnChange({
        title: pageTitle,
        content: JSON.stringify(editor.getJSON()),
      });
    },
  });

  useEffect(() => {
    if (!tiptapEditor) {
      return;
    }

    // Focus the title when the editor is ready
    if (!pageTitle) {
      webEditorRef.current?.focusTitle();
    }
  }, [tiptapEditor, pageTitle]);

  const handleTitleChange = (title: string) => {
    setPageTitle(title);
    debouncedOnChange({
      title,
      content: JSON.stringify(tiptapEditor.getJSON()),
    });
  };

  const handleChoosePhotos = () => {
    setShowAssetPicker(true);
  };

  const handlePdfExportClick = async (options: PagePdfExportConfig) => {
    handlePdfExport({
      pageId: page.id,
      options,
      onSuccess: () => {
        setIsPdfExportOpen(false);
      },
    });
  };

  return (
    <div ref={containerRef}>
      <PageHeader>
        <HeaderTitle>
          <Text as="span" size="s" data-testid="page-modal__header">
            {pageTitle}
          </Text>
        </HeaderTitle>
        <PageStatusDiv>
          {showAutoSave ? (
            <SaveIndicator>
              <Text as="p" size="xs">
                <i
                  className="mdi mdi-cloud-check-variant"
                  style={{ marginRight: 6 }}
                />
                {translate('projectPage.changesSaved.success')}
              </Text>
            </SaveIndicator>
          ) : (
            <Text as="span" color="subtle" size="xs">
              {page.updatedAt
                ? `${translate('projectPage.changesSaved.edited')} ${formatDate(
                    new Date(page.updatedAt),
                    'MMM d h:mm:ss a',
                  )}`
                : null}
            </Text>
          )}
        </PageStatusDiv>
        <ProjectPageShareActions
          pageId={pageNodeId}
          shared={page?.shared}
          shareUrl={page?.shareUrl}
        />

        <ProjectPageMenuActions
          projectDbId={projectDbId}
          pageDbId={page.id}
          onDeleteSuccess={onDelete}
          onExportPdf={() => setIsPdfExportOpen(true)}
          onShowTranscript={() => {
            setIsShowingTranscript((prev) => {
              if (!prev) {
                steven.elementClicked({
                  id: 'ai_note_view_transcript_button',
                  content: translate('projectPage.viewTranscript'),
                  type: 'button',
                  action: 'ai_note_view_transcript_button_clicked',
                });
              }
              return !prev;
            });
          }}
          isShowingTranscript={isShowingTranscript}
          hasTranscript={!!hasTranscripts}
          dataTestIdPrefix="project-page"
        />

        {!hasOnboardedPageTemplates &&
        isPageTemplatesEnabled &&
        canSaveAsTemplate ? (
          <CCTooltip
            position="bottom-end"
            triggerStyle="always"
            onClickOutside={() => setHasOnboardedPageTemplates(true)}
          >
            <Text as="p" size="s" weight="700" family="heading">
              {translate(
                'projectPage.usePageTemplateModal.onboardTooltipMessage',
              )}
            </Text>
          </CCTooltip>
        ) : null}
      </PageHeader>

      {hasTranscripts && isShowingTranscript ? (
        <PageTranscript
          onClosePress={() => setIsShowingTranscript(false)}
          transcripts={transcripts}
          creatorName={captureOutputRequest.creator.fullName}
          createdAt={page.createdAt}
        />
      ) : null}

      {tiptapEditor ? (
        <TipTapContainer data-testid="page-modal__body">
          <WebEditor
            title={pageTitle}
            editor={tiptapEditor}
            ref={webEditorRef}
            onTitleChange={handleTitleChange}
            isLightGalleryEnabled // TODO: Update to use our own AssetGallery
          />
          <PageAssetPickerModal
            editor={tiptapEditor}
            isOpen={showAssetPicker}
            projectNodeId={projectNodeId}
            onRequestClose={() => setShowAssetPicker(false)}
          />
          <PageToolbar
            editor={tiptapEditor}
            onChooseGalleryPhoto={handleChoosePhotos}
          />
        </TipTapContainer>
      ) : null}
      <PagePdfExportModal
        isOpen={isPdfExportOpen}
        onRequestClose={() => {
          setIsPdfExportOpen(false);
        }}
        onExportClick={handlePdfExportClick}
        loading={isExporting}
      />
    </div>
  );
};

export default ProjectPage;
